import { Icon } from '@iconify/react';
import { Braces } from 'lucide-react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ApiDataFieldKey,
  ApiDataTable,
  HttpItemCategory,
  JsonSwitchProps,
} from '@/Domain/components/Action/ApiDataTable/ApiDataTable';
import { TestResponse } from '@/Domain/components/Action/HttpRequestActionSheet/TestResponse/TestResponse';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/Domain/components/Shared/Tabs/Tabs';
import { HttpActionForm } from '@/Domain/hooks/Action/ApiAction/useHttpRequestActionFormV2.hook';

enum TabEnum {
  Headers = 'headers',
  Body = 'body',
  Query = 'query',
  Test = 'test',
}

type Props = {
  form: UseFormReturn<HttpActionForm>;
  actionId?: number;
};

export const ApiData = ({ form, actionId }: Props) => {
  const { t } = useTranslation('actions_page');

  const headers = useWatch({ name: HttpItemCategory.HEADERS });
  const body = useWatch({ name: HttpItemCategory.BODY_ITEMS });
  const query = useWatch({ name: HttpItemCategory.QUERY_ITEMS });
  const isBodyJsonField = useWatch({ name: 'body_items_json_enable' });

  const bodyJsonSwitchParams: JsonSwitchProps = {
    formName: 'body_items_json_enable',
    isActive: isBodyJsonField,
  };

  return (
    <Tabs defaultValue={TabEnum.Headers}>
      <TabsList>
        <TabsTrigger value={TabEnum.Headers}>
          <Icon icon="mingcute:code-line" /> {t('sheet.form.api_endpoint.api_data.tabs.headers')}{' '}
          <div className="flex w-6 items-center justify-center rounded-full bg-accent py-px text-xs text-foreground">
            <span>{headers.length}</span>
          </div>
        </TabsTrigger>
        <TabsTrigger value={TabEnum.Body}>
          <Icon icon="mingcute:braces-line" />
          {t('sheet.form.api_endpoint.api_data.tabs.body')}
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-accent py-px text-xs text-foreground">
            {isBodyJsonField ? <Braces className="h-3.5" /> : <span>{body.length}</span>}
          </div>
        </TabsTrigger>
        <TabsTrigger value={TabEnum.Query}>
          <Icon icon="mingcute:tower-crane-line" />
          {t('sheet.form.api_endpoint.api_data.tabs.query_params')}
          <div className="flex w-6 items-center justify-center rounded-full bg-accent py-px text-xs text-foreground">
            <span>{query.length}</span>
          </div>
        </TabsTrigger>
        <TabsTrigger value={TabEnum.Test}>
          <Icon icon="mingcute:test-tube-line" />
          {t('sheet.form.api_endpoint.api_data.tabs.test')}
        </TabsTrigger>
      </TabsList>
      <TabsContent value={TabEnum.Headers} className="mt-4">
        <ApiDataTable name={HttpItemCategory.HEADERS} form={form} withInsertBadge={true} />
      </TabsContent>
      <TabsContent value={TabEnum.Body} forceMount asChild>
        <section className="mt-4 data-[state=inactive]:hidden">
          <ApiDataTable
            name={HttpItemCategory.BODY_ITEMS}
            form={form}
            dataFieldKey={ApiDataFieldKey.CONTENT}
            withInsertBadge={true}
            jsonSwitchParams={bodyJsonSwitchParams}
          />
        </section>
      </TabsContent>
      <TabsContent value={TabEnum.Query} className="mt-4">
        <ApiDataTable name={HttpItemCategory.QUERY_ITEMS} form={form} withInsertBadge={true} />
      </TabsContent>
      <TabsContent value={TabEnum.Test} forceMount asChild>
        <section className="mt-4 data-[state=inactive]:hidden">
          <TestResponse form={form} actionId={actionId} />
        </section>
      </TabsContent>
    </Tabs>
  );
};
